import styled from "@emotion/styled";
import { evalCondition, notifyParent } from "mailup-react-helpers";
import {
  Card,
  Flex,
  GRID_VARIANT_VERTICAL_24,
  Grid,
  GridCell,
  LINK_VARIANT_DEFAULT,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  SPACING_16,
  SPACING_24,
  SpacingVertical,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_ITALIC_LIGHT,
  TEXT_VARIANT_SMALL,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
  TITLE_VARIANT_M,
  Title,
} from "mailup-web-components";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRecoilCallback, useRecoilValue } from "recoil";

import Header from "./Header";

//  import './ImportLanding.scss'

import IconExclamation from "Components/icons/IconExclamationSolid";
import Page from "Containers/Page";
import importApp from "../../icons/import-integration-sync.svg";
import importIndividual from "../../icons/import-single-recipient.svg";
import uploadfileIcon from "../../icons/import-upload-file.svg";
import copyPasteIcon from "../../icons/import-copy-paste.svg";
import {
  initPageCallback,
  resetPageCallback,
} from "Pages/ImportLanding/callbacks";
import {
  errorState,
  importOptionsState,
  integrationsState,
} from "Pages/ImportLanding/state";
import DataSyncPromoImport from "Widgets/DataSyncPromoImport/DataSyncPromoImport";
import { STORE_KEY_FEATURES, storeGet } from "mailup-react-helpers";
import { gainsightTrack } from "mailup-react-helpers";

const StyledIconExclamation = styled(IconExclamation)`
  display: inline-flex;
  position: relative;
  top: 0.2em;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 250px;
`;

const StyledCardContent = styled(Flex)`
  flex: 1;
  height: 100%;
  justify-content: space-around;
`;

export const ImportLanding = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const initPage = useRecoilCallback(initPageCallback, []);
  const resetPage = useRecoilCallback(resetPageCallback, []);
  const { items, totalItems, whiteLabel, ficStatus } =
    useRecoilValue(integrationsState);
  const options = useRecoilValue(importOptionsState);
  const error = useRecoilValue(errorState);

  const hasFetarureNewImportWizardFile =
    storeGet(STORE_KEY_FEATURES).indexOf("NewImportWizardFile") >= 0;

  const noImport = evalCondition("uo:NOIMPORT");

  useEffect(() => {
    gainsightTrack("mup_import_visitNewImportPage", {});

    initPage();

    return () => {
      resetPage();
    };
  }, [resetPage, initPage]);

  const handleImportFattureInCloud = () => {
    if (ficStatus === "CONNECTED") {
      navigate("/recipients/import-fatture-in-cloud");
    }
  };

  const handleConfigFattureInCloud = () => {
    notifyParent("redirectMainFrame", [
      `settings_app_redirector.aspx?r=${encodeURIComponent(
        "/settings/integrations/connectors/fatture-in-cloud-connector",
      )}`,
    ]);
  };

  const handleSingleImport = () => {
    navigate("/recipients/list?nr=true");
  };

  const goToFileWizard = () => {
    gainsightTrack("mup_import_clickFileUploadInPageNew", {});

    navigate("/recipients/import/file");
  };

  const goToCopyAndPasteWizard = () => {
    navigate("/recipients/import/copyandpaste");
  };

  return (
    <>
      <Header />
      <Page onReload={initPage}>
        <DataSyncPromoImport />

        <SpacingVertical variant={SPACING_24} />

        <Flex flexDirection="column" gap="32px">
          {hasFetarureNewImportWizardFile && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "32px",
                width: "100%",
              }}
            >
              {ficStatus !== undefined && (
                <StyledCard
                  onClick={handleImportFattureInCloud}
                  data-mu-test="import-fatture-in-cloud"
                  disabled={ficStatus === "DISCONNECTED"}
                >
                  <StyledCardContent
                    flexDirection="column"
                    gap="10px"
                    alignItems="center"
                  >
                    <img
                      src={importApp}
                      style={{ height: "90px", width: "90px" }}
                    />

                    <Text variant={TEXT_VARIANT_BOLD}>
                      {intl.formatMessage({
                        id: "Load.ImportLandingPage.ImportFattureInCloudTitle",
                      })}
                    </Text>
                    {ficStatus === "CONNECTED" && (
                      <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                        {intl.formatMessage({
                          id: "Load.ImportLandingPage.ImportFattureInCloudDescription",
                        })}
                      </Text>
                    )}
                    {ficStatus === "DISCONNECTED" && (
                      <Text variant={TEXT_VARIANT_SMALL} htmlElement="div">
                        <StyledIconExclamation width="18px" height="18px" />{" "}
                        {intl.formatMessage(
                          {
                            id: "Load.ImportLandingPage.ImportFattureInCloudDisconnected",
                          },
                          {
                            link: (chunks) => (
                              <Link
                                variant={LINK_VARIANT_DEFAULT}
                                data-mu-test="fatture-in-cloud-disconnected"
                                onClick={handleConfigFattureInCloud}
                              >
                                {chunks}
                              </Link>
                            ),
                          },
                        )}
                      </Text>
                    )}
                  </StyledCardContent>
                </StyledCard>
              )}
              <StyledCard
                onClick={handleSingleImport}
                data-mu-test="singleImport"
              >
                <StyledCardContent
                  flexDirection="column"
                  gap="10px"
                  alignItems="center"
                >
                  <img
                    src={importIndividual}
                    style={{ height: "90px", width: "90px" }}
                  />

                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: "Load.FileImport.AddSingleRecipientDescr",
                    })}
                  </Text>
                  <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                    {intl.formatMessage({
                      id: "Load.FileImport.AddSingleRecipientTitle",
                    })}
                  </Text>
                </StyledCardContent>
              </StyledCard>
              {!noImport && (
                <>
                  <StyledCard onClick={goToFileWizard}>
                    <StyledCardContent
                      flexDirection="column"
                      gap="10px"
                      alignItems="center"
                    >
                      {/** biome-ignore lint/a11y/useAltText: <explanation> */}
                      <img
                        src={uploadfileIcon}
                        style={{ height: "90px", width: "90px" }}
                      />

                      <Text variant={TEXT_VARIANT_BOLD}>
                        {intl.formatMessage({
                          id: "Load.FileImport.AddFileTitle",
                        })}
                      </Text>
                      <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                        {intl.formatMessage({
                          id: "Load.FileImport.AddFileDescr",
                        })}
                      </Text>
                    </StyledCardContent>
                  </StyledCard>
                  <StyledCard onClick={goToCopyAndPasteWizard}>
                    <StyledCardContent
                      flexDirection="column"
                      gap="10px"
                      alignItems="center"
                    >
                      {/** biome-ignore lint/a11y/useAltText: <explanation> */}
                      <img
                        src={copyPasteIcon}
                        style={{ height: "90px", width: "90px" }}
                      />

                      <Text variant={TEXT_VARIANT_BOLD}>
                        {intl.formatMessage({
                          id: "Load.FileImport.CopyAndPasteTitle",
                        })}
                      </Text>
                      <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                        {intl.formatMessage({
                          id: "Load.FileImport.CopyAndPasteDescr",
                        })}
                      </Text>
                    </StyledCardContent>
                  </StyledCard>
                </>
              )}
            </div>
          )}
          {!hasFetarureNewImportWizardFile && (
            <Grid variantVertical={GRID_VARIANT_VERTICAL_24} cols={12}>
              {ficStatus !== undefined && (
                <GridCell span={3}>
                  <Card
                    onClick={handleImportFattureInCloud}
                    data-mu-test="import-fatture-in-cloud"
                    disabled={ficStatus === "DISCONNECTED"}
                  >
                    <Flex flexDirection="column" gap="10px" alignItems="center">
                      <img
                        src={importApp}
                        style={{ height: "90px", width: "90px" }}
                      />

                      <Text variant={TEXT_VARIANT_BOLD}>
                        {intl.formatMessage({
                          id: "Load.ImportLandingPage.ImportFattureInCloudTitle",
                        })}
                      </Text>
                      {ficStatus === "CONNECTED" && (
                        <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                          {intl.formatMessage({
                            id: "Load.ImportLandingPage.ImportFattureInCloudDescription",
                          })}
                        </Text>
                      )}
                      {ficStatus === "DISCONNECTED" && (
                        <Text variant={TEXT_VARIANT_SMALL} htmlElement="div">
                          <StyledIconExclamation width="18px" height="18px" />{" "}
                          {intl.formatMessage(
                            {
                              id: "Load.ImportLandingPage.ImportFattureInCloudDisconnected",
                            },
                            {
                              link: (chunks) => (
                                <Link
                                  variant={LINK_VARIANT_DEFAULT}
                                  data-mu-test="fatture-in-cloud-disconnected"
                                  onClick={handleConfigFattureInCloud}
                                >
                                  {chunks}
                                </Link>
                              ),
                            },
                          )}
                        </Text>
                      )}
                    </Flex>
                  </Card>
                </GridCell>
              )}

              <GridCell span={3}>
                <Card onClick={handleSingleImport} data-mu-test="singleImport">
                  <Flex flexDirection="column" gap="10px" alignItems="center">
                    <img
                      src={importIndividual}
                      style={{ height: "90px", width: "90px" }}
                    />

                    <Text variant={TEXT_VARIANT_BOLD}>
                      {intl.formatMessage({
                        id: "Load.ImportLandingPage.lblSingleSub",
                      })}
                    </Text>
                    <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                      {intl.formatMessage({
                        id: "Load.ImportLandingPage.lblSingleSubDesc",
                      })}
                    </Text>
                  </Flex>
                </Card>
              </GridCell>

              {options.map(
                ({ img, cta, text, dataAttributeTest, onClick }, i) => (
                  <GridCell span={3} key={`new-message-option-${i}`}>
                    <Card onClick={onClick} data-mu-test={dataAttributeTest}>
                      <Flex
                        flexDirection="column"
                        gap="10px"
                        alignItems="center"
                      >
                        {img && (
                          <img
                            src={img}
                            style={{ height: "90px", width: "90px" }}
                          />
                        )}
                        <Text variant={TEXT_VARIANT_BOLD}>{cta}</Text>
                        <Text variant={TEXT_VARIANT_SMALL_LIGHT}>{text}</Text>
                      </Flex>
                    </Card>
                  </GridCell>
                ),
              )}
            </Grid>
          )}

          {!whiteLabel && (
            <section>
              {error && (
                <>
                  <Title variant={TITLE_VARIANT_M}>
                    {intl.formatMessage({
                      id: "Load.ImportLandingPage.lblImportIntegration",
                    })}
                  </Title>
                  <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement="p">
                    {intl.formatMessage({ id: "Load.Common.LoadDataError" })}{" "}
                    <Link variant={LINK_VARIANT_EXTRALIGHT} onClick={initPage}>
                      {intl.formatMessage({ id: "Load.Common.RealoadPage" })}
                    </Link>
                  </Text>
                </>
              )}
              {!error && totalItems > 0 && (
                <>
                  <Title variant={TITLE_VARIANT_M}>
                    {intl.formatMessage({
                      id: "Load.ImportLandingPage.lblImportIntegration",
                    })}
                  </Title>
                  <SpacingVertical variant={SPACING_16} />
                  <Grid cols={12} variantVertical={GRID_VARIANT_VERTICAL_24}>
                    {items.map(
                      (
                        { logo, title, excerpt, dataAttributeTest, url, cb },
                        i,
                      ) => (
                        <GridCell span={3} key={`new-message-option-${i}`}>
                          <Card
                            onClick={() => {
                              if (url !== undefined) {
                                window.open(
                                  url,
                                  "_blank",
                                  "noreferrer,noopener",
                                );
                              } else if (typeof cb === "function") {
                                cb();
                              }
                            }}
                            data-mu-test={dataAttributeTest}
                          >
                            <Flex
                              flexDirection="column"
                              gap="10px"
                              alignItems="center"
                            >
                              {logo && (
                                <img
                                  src={logo}
                                  style={{
                                    width: 71,
                                    height: 71,
                                  }}
                                />
                              )}
                              <Text variant={TEXT_VARIANT_BOLD}>{title}</Text>
                              <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                                {excerpt}
                              </Text>
                            </Flex>
                          </Card>
                        </GridCell>
                      ),
                    )}
                  </Grid>
                </>
              )}
            </section>
          )}
        </Flex>
      </Page>
    </>
  );
};
